import { Controller } from "@hotwired/stimulus";

// Synchronizes search form state between hero and header forms
export default class extends Controller {
  static targets = ["heroForm", "headerForm"];

  connect() {
    this.syncing = false;
    this._fieldChangedHandler = this.handleFieldChanged.bind(this);
    window.addEventListener("search-field:changed", this._fieldChangedHandler);
  }

  disconnect() {
    window.removeEventListener("search-field:changed", this._fieldChangedHandler);
  }

  handleFieldChanged(event) {
    if (this.syncing) return;

    const { field, value, source } = event.detail;
    if (!field || !source) return;

    // Determine which form to sync to
    const isFromHero = this.hasHeroFormTarget && this.heroFormTarget.contains(source);
    const isFromHeader = this.hasHeaderFormTarget && this.headerFormTarget.contains(source);

    if (!isFromHero && !isFromHeader) return;

    const targetForm = isFromHero ? this.headerFormTarget : this.heroFormTarget;
    if (!targetForm) return;

    this.syncing = true;

    try {
      if (field === "activities") {
        this.syncActivities(targetForm, value);
      } else if (field === "address") {
        this.syncAddress(targetForm, value);
      } else if (field === "age") {
        this.syncAge(targetForm, value);
      }
    } finally {
      this.syncing = false;
    }
  }

  syncActivities(targetForm, selectedIds) {
    const activitySelector = targetForm.querySelector("[data-controller='activity-selector']");
    if (!activitySelector) return;

    // Get all activity checkboxes in target form
    const allCheckboxes = activitySelector.querySelectorAll("input[type='checkbox'][name='activity_id[]']");

    allCheckboxes.forEach((cb) => {
      const shouldBeChecked = selectedIds.includes(cb.value);
      if (cb.checked !== shouldBeChecked) {
        cb.checked = shouldBeChecked;
      }
    });

    // Also update parent checkboxes based on their children
    const parentCheckboxes = activitySelector.querySelectorAll("[data-activity-selector-target='parentCheckbox']");
    parentCheckboxes.forEach((parentCb) => {
      const parentId = parentCb.value;
      const childCheckboxes = activitySelector.querySelectorAll(`input[data-parent-id="${parentId}"]`);
      const allChildrenChecked = Array.from(childCheckboxes).every((cb) => cb.checked);
      const someChildrenChecked = Array.from(childCheckboxes).some((cb) => cb.checked);
      parentCb.checked = allChildrenChecked;

      // Update visual style on parent pill button
      const parentPill = activitySelector.querySelector(`[data-activity-selector-target="parentPill"][data-parent-id="${parentId}"]`);
      if (parentPill) {
        if (someChildrenChecked) {
          parentPill.classList.add("border-[#9565fe]", "text-[#9565fe]", "bg-[#ebe2ff]");
        } else {
          parentPill.classList.remove("border-[#9565fe]", "text-[#9565fe]", "bg-[#ebe2ff]");
        }
      }

      // Update "Tout sélectionner" / "Tout désélectionner" button text
      const allToggle = activitySelector.querySelector(`[data-activity-selector-target="allToggle"][data-parent-id="${parentId}"]`);
      if (allToggle) {
        const buttonText = allToggle.querySelector("span");
        if (buttonText) {
          buttonText.textContent = allChildrenChecked
            ? allToggle.dataset.deselectAllText
            : allToggle.dataset.selectAllText;
        }
      }
    });

    // Update parent count badges
    this.updateParentCountBadges(activitySelector);

    // Update display
    this.updateActivitySelectorDisplay(activitySelector, selectedIds.length);
  }

  updateParentCountBadges(activitySelector) {
    const parentCountBadges = activitySelector.querySelectorAll("[data-activity-selector-target='parentCount']");
    parentCountBadges.forEach((badge) => {
      const parentId = badge.dataset.parentId;
      const childCheckboxes = activitySelector.querySelectorAll(`input[data-parent-id="${parentId}"]`);
      const checkedCount = Array.from(childCheckboxes).filter((cb) => cb.checked).length;

      if (checkedCount > 0) {
        badge.textContent = checkedCount;
        badge.classList.remove("hidden");
        badge.classList.add("flex");
      } else {
        badge.textContent = "";
        badge.classList.add("hidden");
        badge.classList.remove("flex");
      }
    });
  }

  syncAddress(targetForm, value) {
    const addressSelector = targetForm.querySelector("[data-controller='address-selector']");
    if (!addressSelector) return;

    const input = addressSelector.querySelector("[data-address-selector-target='input']");
    if (input) {
      input.value = value;
    }

    // Update display value
    const displayValue = addressSelector.querySelector("[data-address-selector-target='displayValue']");
    if (displayValue) {
      if (value) {
        const parts = value.split(",").map(s => s.trim());
        const displayText = parts.length >= 2 ? parts[1] : value;
        displayValue.textContent = displayText;
        displayValue.classList.remove("hidden");
      } else {
        displayValue.textContent = "";
        displayValue.classList.add("hidden");
      }
    }
  }

  syncAge(targetForm, value) {
    const ageSelector = targetForm.querySelector("[data-controller='age-selector']");
    if (!ageSelector) return;

    const input = ageSelector.querySelector("[data-age-selector-target='input']");
    if (input) {
      input.value = value;
    }

    // Update display value
    const displayValue = ageSelector.querySelector("[data-age-selector-target='displayValue']");
    if (displayValue) {
      if (value) {
        const oneYearText = ageSelector.dataset.ageSelectorOneYearValue || "1 an";
        const multipleYearsText = ageSelector.dataset.ageSelectorMultipleYearsValue || "%{count} ans";
        displayValue.textContent = value === "1" ? oneYearText : multipleYearsText.replace("%{count}", value);
        displayValue.classList.remove("hidden");
      } else {
        displayValue.textContent = "";
        displayValue.classList.add("hidden");
      }
    }
  }

  updateActivitySelectorDisplay(activitySelector, count) {
    const displayValue = activitySelector.querySelector("[data-activity-selector-target='displayValue']");
    if (!displayValue) return;

    if (count === 0) {
      displayValue.textContent = "";
      displayValue.classList.add("hidden");
    } else {
      const formatString = activitySelector.dataset.activitySelectorSelectedFormatValue || "%{count} sélect.";
      displayValue.textContent = formatString.replace("%{count}", count);
      displayValue.classList.remove("hidden");
    }
  }
}
